'use client'

import React from 'react'
import { cx } from 'class-variance-authority'
import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { XCircleIcon } from '@phosphor-icons/react/dist/ssr/XCircle'
import Async from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import { GroupBase, OptionsOrGroups } from 'react-select'
import { UseFormReset, UseFormReturn, useWatch } from 'react-hook-form'

import type { Field, LabelType } from '@skop-app/lib-domain/types'
import { Checkbox } from '@/components/atoms/Checkbox/checkbox.atom'
import { Input } from '@/components/atoms/Input/input.atom'
import { InputPassword } from '@/components/atoms/InputPassword/InputPassword.atom'
import { Loader } from '@/components/atoms/Loader/loader.atom'
import { Select } from '@/components/atoms/Select/select.atom'
import { StepperInput } from '@/components/atoms/StepperInput/StepperInput.atom'
import { Switch } from '@/components/atoms/Switch/switch.atom'
import { TextArea } from '@/components/atoms/TextArea/textArea.atom'
import { Form } from '@/components/molecules/Form/form.molecule'
import {
  MenuList,
  overflowProps,
  reactSelectStyles,
} from '@/components/atoms/Select/select.config'
import { filterDomProps } from '@skop-app/lib-domain/utils'
import { DynamicImageUploadField } from './DynamicImageUploadField'
import {
  formatDateInputValue,
  getFriendlyFieldErrorMessage,
  getValueAtPath,
  hasVisibleDependencyValue,
  normalizeFieldChangeValue,
} from './dynamicForm.helpers'

type DynamicFormVariants = string

const LazyTextEditor = React.lazy(
  () => import('@/components/molecules/TextEditor/textEditor.molecule'),
)

const TextEditorFallback = () => (
  <div
    className='h-40 w-full rounded-md border border-zinc-300 bg-zinc-50'
    aria-hidden='true'
  />
)

const DEFAULT_COLOR_VALUE = '#94a3b8'

const normalizeColorValue = (value: unknown) => {
  if (typeof value !== 'string') return DEFAULT_COLOR_VALUE

  const trimmedValue = value.trim()
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmedValue)
    ? trimmedValue
    : DEFAULT_COLOR_VALUE
}

type FieldRenderProps = {
  field: Field
  form: UseFormReturn<any>
  index?: number
  variant?: DynamicFormVariants
  showError?: boolean
  isScrollable?: boolean
}

export const DynamicFormFieldRender = ({
  field,
  form,
  index = 0,
  variant,
  showError = true,
  isScrollable = false,
}: FieldRenderProps) => {
  const { label, className, transform, visibilityDependsOn, ...fieldProps } =
    field

  const dependentValue = useWatch({
    control: form.control,
    name: (visibilityDependsOn ??
      '__dynamic_form_visibility_dependency__') as any,
    disabled: !visibilityDependsOn,
  })

  if (visibilityDependsOn && !hasVisibleDependencyValue(dependentValue)) {
    return null
  }

  return (
    <Form.Field
      key={field.name}
      name={field.name}
      control={form.control}
      render={({ field: { onChange, onBlur, value } }) => {
        const fieldError = getValueAtPath(form.formState.errors, field.name)
        const friendlyErrorMessage = getFriendlyFieldErrorMessage(
          field,
          fieldError,
        )

        const handleChange = (incomingValue: any) => {
          const normalizedValue = normalizeFieldChangeValue(
            field,
            incomingValue,
          )
          const finalValue = transform
            ? transform(normalizedValue)
            : normalizedValue
          onChange(finalValue)
        }

        return (
          <Form.Item className={cx('w-full', className)}>
            <Form.Control>
              <DynamicField
                field={fieldProps}
                onChange={handleChange}
                onBlur={onBlur}
                reset={form.reset}
                value={value}
                error={fieldError}
                label={label}
                index={index}
                variant={variant}
                isScrollable={isScrollable}
              >
                {label && (
                  <Form.Label aria-required={field['aria-required']}>
                    {label}
                  </Form.Label>
                )}
              </DynamicField>
            </Form.Control>
            {showError && (
              <Form.Message className='!p-2'>
                {friendlyErrorMessage}
              </Form.Message>
            )}
          </Form.Item>
        )
      }}
    />
  )
}

type DynamicFieldProps = {
  field: Field
  onChange: (e: any) => void
  onBlur: (e: any) => void
  error: any
  index: number
  children: React.ReactNode
  value: any
  label?: string | React.ReactNode
  variant?: DynamicFormVariants
  reset?: UseFormReset<any>
  isScrollable?: boolean
} & Record<string, any>

const DynamicField = ({
  field,
  onChange,
  onBlur,
  value,
  error,
  index,
  children,
  label,
  variant,
  isScrollable = false,
  ...slotControlProps
}: DynamicFieldProps) => {
  const {
    showValidationMark,
    isClearable,
    validation: _validation,
    overflow,
    getOptionLabel,
    getOptionValue,
    onChange: externalOnChange,
    onBlur: externalOnBlur,
    ...rest
  } = field

  const domProps = filterDomProps(rest)
  const controlId =
    typeof slotControlProps.id === 'string' ? slotControlProps.id : field.name
  const controlA11yProps = {
    'aria-describedby': slotControlProps['aria-describedby'],
    'aria-invalid': slotControlProps['aria-invalid'],
  }
  const reactSelectVariant = variant === 'tertiary' ? 'tertiary' : 'default'
  const inputVariant = variant === 'default' ? 'default' : undefined
  const checkboxVariant =
    variant === 'default' || variant === 'error' ? variant : undefined
  const switchVariant = variant === 'default' ? 'default' : undefined
  const textAreaColor = variant === 'tertiary' ? 'tertiary' : undefined

  const shouldOverflow = overflow || isScrollable
  const isColorField = field.type === 'color'
  const normalizedColorValue = isColorField ? normalizeColorValue(value) : null

  const variants = {
    default: {
      primary25: '#ffe7b3',
      primary50: '#fcd37e',
      primary75: '#ffcf6b',
      primary: '#ffc857',
    },
    tertiary: {
      primary25: '#e3f8ff',
      primary50: '#9de3f8',
      primary75: '#6cd9f0',
      primary: '#2B70B1',
    },
  }
  const reactSelectColors = variants[reactSelectVariant]

  const reactSelectTheme = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      ...reactSelectColors,
    },
  })

  const handleFieldChange = (...args: any[]) => {
    onChange(args[0])
    externalOnChange?.(...args)
  }

  const handleFieldBlur = (...args: any[]) => {
    onBlur(args[0])
    externalOnBlur?.(...args)
  }

  React.useEffect(() => {
    if (!isColorField || normalizedColorValue === null) {
      return
    }

    if (value !== normalizedColorValue) {
      onChange(normalizedColorValue)
    }
  }, [isColorField, normalizedColorValue, onChange, value])

  switch (field.type) {
    case 'image-upload':
      return (
        <>
          {index < 1 && children}
          <DynamicImageUploadField
            id={controlId}
            name={field.name}
            field={{ ...field, label } as Field}
            value={value}
            error={error}
            disabled={field.disabled}
            ariaDescribedBy={controlA11yProps['aria-describedby']}
            ariaInvalid={controlA11yProps['aria-invalid']}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'text':
    case 'password':
    case 'email':
    case 'number':
    case 'date':
    case 'time':
      return (
        <>
          {index < 1 && children}
          <div className='relative'>
            {field.type === 'password' ? (
              <InputPassword
                {...domProps}
                id={controlId}
                name={field.name}
                value={typeof value === 'string' ? value : ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                variant={variant}
                showPasswordStrength={field.showPasswordStrength}
                {...controlA11yProps}
                {...(field['aria-required'] &&
                  domProps['placeholder'] && {
                    placeholder: `${domProps['placeholder']} *`,
                  })}
              />
            ) : (
              <Input
                {...domProps}
                id={controlId}
                name={field.name}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                value={
                  field.type === 'date'
                    ? formatDateInputValue(value)
                    : (value ?? '')
                }
                variant={inputVariant}
                {...controlA11yProps}
                {...(field['aria-required'] &&
                  domProps['placeholder'] && {
                    placeholder: `${domProps['placeholder']} *`,
                  })}
              />
            )}
            {showValidationMark && !error && value && (
              <CheckCircleIcon
                className='absolute right-8 top-[7px] size-6 text-green-500'
                aria-hidden='true'
              />
            )}
            {showValidationMark && error && (
              <XCircleIcon
                className='absolute right-8 top-[7px] size-6 text-red-500'
                aria-hidden='true'
              />
            )}
            {isClearable && value && (
              <button
                type='button'
                onClick={() => handleFieldChange('')}
                className='absolute right-8 top-[13px] size-4 text-slate-300 hover:text-slate-500'
                aria-label='Effacer'
              >
                <XIcon className='size-4' aria-hidden='true' weight='bold' />
              </button>
            )}
          </div>
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'color': {
      return (
        <>
          {index < 1 && children}
          <div className='relative flex items-center gap-3 rounded-md border border-border bg-slate-50 px-3 py-2'>
            <label
              htmlFor={controlId}
              className={cx(
                'relative block size-10 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-white',
                field.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
              style={{ backgroundColor: normalizedColorValue }}
            >
              <span className='sr-only'>{field.label ?? field.name}</span>
              <input
                {...domProps}
                {...controlA11yProps}
                id={controlId}
                name={field.name}
                type='color'
                value={normalizedColorValue}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
              />
            </label>

            <div className='min-w-0'>
              <div className='text-sm font-medium text-slate-700'>
                {normalizedColorValue.toUpperCase()}
              </div>
              {field.placeholder && (
                <div className='mt-1 text-sm text-slate-400'>
                  {field.placeholder}
                </div>
              )}
            </div>
          </div>
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    }
    case 'select':
      return (
        <>
          {index < 1 && children}
          <Select
            id={field.name}
            inputId={controlId}
            instanceId={controlId}
            name={field.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            value={value}
            options={
              field.options as OptionsOrGroups<
                LabelType | string,
                GroupBase<LabelType | string>
              >
            }
            isDisabled={field.disabled}
            placeholder={field.placeholder ?? ''}
            noOptionsMessage={() => 'Aucun résultat'}
            theme={reactSelectTheme}
            styles={reactSelectStyles}
            isClearable={isClearable}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            {...controlA11yProps}
            {...overflowProps(shouldOverflow)}
            {...domProps}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'select-create':
      return (
        <>
          {index < 1 && children}
          <CreatableSelect
            id={field.name}
            inputId={controlId}
            instanceId={controlId}
            name={field.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            value={value}
            options={
              field.options as OptionsOrGroups<
                LabelType | string,
                GroupBase<LabelType | string>
              >
            }
            isDisabled={field.disabled}
            placeholder={field.placeholder ?? ''}
            formatCreateLabel={(inputValue) => `Ajouter "${inputValue}"`}
            theme={reactSelectTheme}
            styles={reactSelectStyles}
            components={{ MenuList }}
            isClearable={isClearable}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            {...controlA11yProps}
            {...overflowProps(shouldOverflow)}
            {...domProps}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'select-async':
      return (
        <>
          {index < 1 && children}
          <Async
            id={field.name}
            inputId={controlId}
            instanceId={controlId}
            name={field.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            value={value}
            loadOptions={field.loadOptions}
            isDisabled={field.disabled}
            placeholder={field.placeholder ?? ''}
            noOptionsMessage={() => 'Aucun résultat'}
            loadingMessage={() => <Loader />}
            theme={reactSelectTheme}
            styles={reactSelectStyles}
            components={{ MenuList }}
            isClearable={isClearable}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            {...controlA11yProps}
            {...overflowProps(shouldOverflow)}
            {...domProps}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'switch':
      return (
        <div className='rounded-lg border bg-white p-4'>
          <div className='flex items-center flex-row space-x-4'>
            <Switch
              id={controlId}
              onCheckedChange={handleFieldChange}
              checked={!!value}
              className={field.description ? 'mt-1.5' : ''}
              variant={switchVariant}
              {...controlA11yProps}
              {...{ ...domProps, type: 'button' }}
            />
            <div className='flex flex-col space-y-0.5 justify-center items-start'>
              <p> {children} </p>
              {field.description && (
                <Form.Description>{field.description}</Form.Description>
              )}
            </div>
          </div>
        </div>
      )
    case 'textarea':
      return (
        <>
          {index < 1 && children}
          <TextArea
            id={controlId}
            name={field.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            value={value || ''}
            color={textAreaColor}
            {...controlA11yProps}
            {...domProps}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'text-editor': {
      const editorDomProps = filterDomProps(rest, ['type']) as Record<
        string,
        unknown
      >

      return (
        <>
          {index < 1 && children}
          <React.Suspense fallback={<TextEditorFallback />}>
            <LazyTextEditor
              id={controlId}
              name={field.name}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              value={typeof value === 'string' ? value : ''}
              placeholder={field.placeholder}
              rows={field.rows ?? field.numberOfLines}
              role='textbox'
              aria-multiline='true'
              {...controlA11yProps}
              {...editorDomProps}
            />
          </React.Suspense>
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    }
    case 'checkbox':
      return (
        <div className='relative flex items-center space-x-2'>
          <Checkbox
            id={controlId}
            name={field.name}
            onChange={handleFieldChange}
            checked={!!value}
            variant={checkboxVariant}
            {...controlA11yProps}
            {...domProps}
          />
          <span className='max-w-full absolute -right-6 -top-1 w-full'>
            {children}
          </span>
        </div>
      )
    case 'stepper':
      return (
        <>
          {index < 1 && children}
          <StepperInput
            id={controlId}
            name={field.name}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            value={
              value === undefined || isNaN(Number(value)) ? 0 : Number(value)
            }
            {...controlA11yProps}
            {...domProps}
          />
          {field.description && (
            <Form.Description>{field.description}</Form.Description>
          )}
        </>
      )
    case 'string':
      return <span className='text-sm text-slate-500'>{children ?? value}</span>
    default:
      return null
  }
}
